import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import Alert from '../components/Alert';
import ToastContainer from '../components/ToastContainer';
import { ToastType } from '../components/Toast';

interface AlertOptions {
  title: string;
  message: string;
  type?: 'success' | 'error' | 'warning' | 'info';
  confirmText?: string;
  cancelText?: string;
  showCancel?: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
}

interface ToastOptions {
  message: string;
  type: ToastType;
  duration?: number;
}

interface NotificationContextType {
  showAlert: (options: AlertOptions) => void;
  showToast: (options: ToastOptions) => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
  confirm: (title: string, message: string, onConfirm: () => void, onCancel?: () => void) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
}

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [alert, setAlert] = useState<(AlertOptions & { isOpen: boolean }) | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showAlert = useCallback((options: AlertOptions) => {
    setAlert({
      ...options,
      isOpen: true,
    });
  }, []);

  const hideAlert = useCallback(() => {
    setAlert(null);
  }, []);

  const handleAlertConfirm = useCallback(() => {
    if (alert?.onConfirm) {
      alert.onConfirm();
    }
    hideAlert();
  }, [alert, hideAlert]);

  const handleAlertClose = useCallback(() => {
    if (alert?.onCancel) {
      alert.onCancel();
    }
    hideAlert();
  }, [alert, hideAlert]);

  const showToast = useCallback((options: ToastOptions) => {
    const id = `toast-${Date.now()}-${Math.random()}`;
    const newToast: Toast = {
      id,
      message: options.message,
      type: options.type,
      duration: options.duration,
    };
    setToasts((prev) => [...prev, newToast]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showSuccess = useCallback((message: string) => {
    showToast({ message, type: 'success', duration: 5000 });
  }, [showToast]);

  const showError = useCallback((message: string) => {
    showToast({ message, type: 'error', duration: 7000 });
  }, [showToast]);

  const showWarning = useCallback((message: string) => {
    showToast({ message, type: 'warning', duration: 6000 });
  }, [showToast]);

  const showInfo = useCallback((message: string) => {
    showToast({ message, type: 'info', duration: 5000 });
  }, [showToast]);

  const confirm = useCallback((
    title: string,
    message: string,
    onConfirm: () => void,
    onCancel?: () => void
  ) => {
    showAlert({
      title,
      message,
      type: 'warning',
      confirmText: 'Confirm',
      cancelText: 'Cancel',
      showCancel: true,
      onConfirm,
      onCancel,
    });
  }, [showAlert]);

  const value: NotificationContextType = {
    showAlert,
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    confirm,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      {alert && alert.isOpen && (
        <Alert
          isOpen={alert.isOpen}
          onClose={handleAlertClose}
          onConfirm={handleAlertConfirm}
          title={alert.title}
          message={alert.message}
          type={alert.type || 'warning'}
          confirmText={alert.confirmText}
          cancelText={alert.cancelText}
          showCancel={alert.showCancel}
        />
      )}
      <ToastContainer toasts={toasts} onClose={removeToast} />
    </NotificationContext.Provider>
  );
};

